import React from "react";
import { SEOHelmet } from "../components/common/SEOHelmet";
import { PATH_ERGOTHERAPIE } from "../utils/constants";

type Section = {
  title: string;
  text?: string;
  content?: string[];
};

const data: { sections: Section[] } = {
  sections: [
    {
      title: "Ergotherapie",
      text: "Ergotherapie befähigt Menschen dazu, verloren gegangene Fähigkeiten wiederzuerlangen oder neue zu entdecken. Sie unterstützt und begleitet Menschen jeden Alters. Ergotherapie betrachtet den Menschen ganzheitlich. Dabei werden: der Alltag, die Freizeit, der Beruf/die Schule und nach Möglichkeit auch enge Bezugspersonen/Angehörige mit einbezogen. Ergotherapie ermöglicht es Ihnen, wieder die Dinge zu tun, die Ihnen wichtig sind. Ergotherapie bietet die Möglichkeit, Alltagstätigkeiten, die ihnen aktuell erschwert sind, wiederzuerlangen.",
    },
    {
      title: "Was uns wichtig ist",
      text: "Jeder Mensch ist individuell. Genauso individuell ist jede Therapie. Sie orientiert sich immer an den Zielen und Bedürfnissen des Klienten:In. Dabei bildet die Basis jeder Therapie eine gute therapeutische Beziehung. Empathie – Akzeptanz - Wertschätzung sind für uns selbstverständlich. Ziel ist es, Menschen ein Stück auf ihrem Lebensweg zu begleiten, sie zu befähigen, wieder selbstbestimmt leben zu können, neue Ressourcen zu entdecken oder verloren gegangene wiederzuerlangen. Den Alltag wieder meistern zu können.",
    },
    {
      title: "Ergotherapie ist",
      content: ["Klientenzentriert", "Alltagsorientiert", "Ressourcen orientiert", "Ganzheitlich orientiert"],
    },
  ],
};

export default function OccupationalTherapyPage() {
  return (
    <>
      <SEOHelmet page="ergotherapie" pathname={PATH_ERGOTHERAPIE} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          "name": "Ergotherapie Rottweil - Lisa Spreitzer",
          "description": "Ganzheitliche Ergotherapie zur Förderung der Selbstständigkeit in Alltag, Beruf und Schule für alle Altersgruppen.",
          "url": "https://ergorw.de/ergotherapie",
          "telephone": "+49-741-89698293",
          "email": "info@ergorw.de",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Auf dem Wall 29",
            "addressLocality": "Rottweil",
            "addressRegion": "Baden-Württemberg",
            "postalCode": "78628",
            "addressCountry": "DE"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "48.1684",
            "longitude": "8.6278"
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "08:00",
              "closes": "18:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Saturday",
              "opens": "07:00",
              "closes": "17:00"
            }
          ],
          "founder": {
            "@type": "Person",
            "name": "Lisa Spreitzer",
            "jobTitle": "Ergotherapeutin"
          },
          "medicalSpecialty": "Occupational Therapy",
          "areaServed": {
            "@type": "City",
            "name": "Rottweil"
          },
          "sameAs": [
            "https://www.instagram.com/ergotherapie_zeittraeume/"
          ]
        })}
      </script>

      <main className="space-y-14">
        {/* Introduction Section */}
        <article>
          <header>
            <h1
              className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6"
              id="main-heading"
            >
              Was ist Ergotherapie?
            </h1>
          </header>
          <section aria-labelledby="main-heading">
            <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">{data.sections[0].text}</p>
          </section>
        </article>

        {/* Additional Sections */}
        {data.sections.slice(1).map((section, index) => (
          <article key={index}>
            <section aria-labelledby={`section-${index}`}>
              <h2
                id={`section-${index}`}
                className="text-xl sm:text-2xl font-semibold tracking-tight text-ergo-600 dark:text-ergo-400 mb-4"
              >
                {section.title}
              </h2>
              {section.text && (
                <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">{section.text}</p>
              )}
              {section.content && (
                <ul
                  className="list-disc marker:text-ergo-400 dark:marker:text-ergo-500 list-inside space-y-2 text-base text-gray-500 dark:text-gray-400"
                  aria-label="Grundprinzipien der Ergotherapie"
                >
                  {section.content.map((item, index) => (
                    <li key={index} className="leading-relaxed">
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </article>
        ))}
      </main>
    </>
  );
}
